import { Session } from "./core/session";
import { DEFAULT_SETTINGS, type AppSettings } from "./core/types";
import { loadSettings, saveSettings, applyTheme } from "./store/settings";
import { getSaveFolder, folderPermission } from "./store/destination";
import { formatBytes } from "./store/takes";
import { mountRecordUi } from "./ui/record";
import { mountLibraryUi } from "./ui/library";
import { mountSettingsUi } from "./ui/settings";
import type { AppContext, Tab } from "./ui/context";
import { el, on, showBanner, hideBanner, toast } from "./ui/dom";
import { embedConfig, mountEmbedBridge } from "./embed/bridge";

declare global {
  interface Window {
    /** Debug handle: the same objects the UI uses, for tooling and embeds. */
    grabi?: {
      session: Session;
      settings: AppSettings;
      showTab: (tab: Tab) => void;
    };
  }
}

const TAB_VIEWS: Record<Tab, string> = {
  record: "view-record",
  library: "view-library",
  settings: "view-settings",
};

async function boot(): Promise<void> {
  let settings: AppSettings = { ...DEFAULT_SETTINGS };
  try {
    settings = await loadSettings();
  } catch (error) {
    console.error("settings could not be read", error);
  }
  applyTheme(settings.theme);

  const canvas = el<HTMLCanvasElement>("stage");
  const session = new Session(canvas, settings);

  let refreshLibraryImpl: () => Promise<void> = async () => {};

  const ctx: AppContext = {
    session,
    settings,
    persist: () => saveSettings(ctx.settings),
    refreshLibrary: () => refreshLibraryImpl(),
    showTab,
  };

  const embed = embedConfig(window.location);
  if (embed) mountEmbedBridge(session, embed);

  function showTab(tab: Tab): void {
    for (const [key, viewId] of Object.entries(TAB_VIEWS) as [Tab, string][]) {
      const view = document.getElementById(viewId);
      if (view) view.hidden = key !== tab;
    }
    for (const button of document.querySelectorAll<HTMLButtonElement>(".tab")) {
      button.setAttribute("aria-selected", String(button.dataset.tab === tab));
    }
    if (tab === "library") void ctx.refreshLibrary();
    if (tab === "settings") void settingsUi.refreshFolder();
  }

  for (const button of document.querySelectorAll<HTMLButtonElement>(".tab")) {
    on(button, "click", () => showTab((button.dataset.tab as Tab) ?? "record"));
  }

  // theme
  on(el<HTMLButtonElement>("theme-toggle"), "click", () => {
    const order: AppSettings["theme"][] = ["auto", "light", "dark"];
    const index = order.indexOf(ctx.settings.theme);
    const next = order[(index + 1) % order.length] ?? "auto";
    ctx.settings.theme = next;
    applyTheme(next);
    void ctx.persist();
    toast(`Theme: ${next}`, "info");
  });

  on(el<HTMLButtonElement>("banner-close"), "click", hideBanner);

  window.grabi = { session, settings: ctx.settings, showTab };

  mountRecordUi(ctx);
  const libraryUi = mountLibraryUi(ctx);
  const settingsUi = mountSettingsUi(ctx);
  refreshLibraryImpl = libraryUi.refresh;

  // ------------------------------------------------------------- startup
  try {
    await session.init();
  } catch (error) {
    console.error("session init failed", error);
    toast("Local storage is unavailable — recordings may not be recoverable.", "error");
  }

  try {
    const folder = await getSaveFolder();
    if (!folder) {
      showBanner("Choose a folder once — takes are written there as they record.", {
        label: "Choose folder",
        run: () => showTab("settings"),
      });
    } else if ((await folderPermission(folder)) !== "granted") {
      showBanner(`“${folder.name}” needs permission again before the next take.`, {
        label: "Allow",
        run: () => showTab("settings"),
      });
    }
  } catch {
    // No folder support: the app still records, into its own storage.
  }

  // Recovery of an interrupted take, before anything else can overwrite it.
  void (async () => {
    try {
      const unfinished = await session.listUnfinished();
      const first = unfinished[0];
      if (!first) return;
      const dialog = el<HTMLDialogElement>("recover-dialog");
      const text = el<HTMLParagraphElement>("recover-text");
      text.textContent =
        `A take from an earlier session was interrupted (${formatBytes(first.bytes)} written). ` +
        "It was written to disk the whole time, so it can still be saved.";
      const keep = el<HTMLButtonElement>("recover-keep");
      const discard = el<HTMLButtonElement>("recover-discard");
      keep.onclick = () => {
        dialog.close();
        void session
          .recover(first.session, first.extension)
          .then((take) => {
            if (take) {
              toast(`Recovered ${take.fileName}`, "ok");
              void ctx.refreshLibrary();
              showTab("library");
            }
          })
          .catch((error: unknown) => {
            toast(error instanceof Error ? error.message : "Recovery failed.", "error");
          });
      };
      discard.onclick = () => {
        dialog.close();
        void session.discardJournal(first.session);
      };
      dialog.showModal();
    } catch (error) {
      console.error("recovery scan failed", error);
    }
  })();

  // A page that is being put away must not leave an encoder running silently.
  window.addEventListener("pagehide", () => {
    if (session.isRecording) void session.finalize("shutdown");
  });

  window.addEventListener("error", (event) => {
    console.error(event.error);
  });
  window.addEventListener("unhandledrejection", (event) => {
    console.error(event.reason);
  });

  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch((error) => {
        console.warn("service worker not registered", error);
      });
    });
  }
}

void boot();
